type TgUser = {
  id: number;
  is_bot?: boolean;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
};

type TgThemeParams = Partial<{
  bg_color: string;
  text_color: string;
  hint_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  secondary_bg_color: string;
  header_bg_color: string;
  accent_text_color: string;
  section_bg_color: string;
  section_header_text_color: string;
  subtitle_text_color: string;
  destructive_text_color: string;
}>;

type TgWebApp = {
  ready: () => void;
  expand: () => void;
  close: () => void;
  platform: string;
  version: string;
  colorScheme: 'light' | 'dark';
  themeParams: TgThemeParams;
  initData: string;
  initDataUnsafe: { user?: TgUser; start_param?: string };
  MainButton: {
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    setParams: (params: { text?: string; color?: string; text_color?: string; is_active?: boolean; is_visible?: boolean }) => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  showPopup: (
    params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive'; text?: string }> },
    callback?: (buttonId: string) => void,
  ) => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  setBottomBarColor?: (color: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
};

/** Фон кабинета, тот же, что `--color-background` в index.css. */
const APP_BACKGROUND = '#0a0a0b';

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export const getWebApp = (): TgWebApp | null => {
  if (typeof window !== "undefined" && window.Telegram?.WebApp) return window.Telegram.WebApp;
  return null;
};

export const getThemeParams = (): TgThemeParams => getWebApp()?.themeParams || {};
export const getTgUser = (): TgUser | null => getWebApp()?.initDataUnsafe?.user ?? null;

export const initTelegram = (): void => {
  const webApp = getWebApp();
  if (webApp) {
    webApp.ready();
    webApp.expand();
    try {
      // Красная, как кнопки кабинета и сайта: один акцент на весь продукт.
      webApp.MainButton.setParams({ color: '#D12F35', text_color: '#ffffff' });
    } catch {
      // older clients
    }
    // Шапка, фон вебвью и полоса под MainButton в цвет кабинета (design-8). Иначе
    // Telegram красит их своей темой: в светлой над чёрным кабинетом висела белая
    // шапка, а снизу белая полоса.
    try {
      // Цвет шапки hex-кодом Telegram понимает с 6.9, фона — с 6.1. Раньше чем 6.9
      // setHeaderColor бросает, и фон за ним не ставился бы.
      if (webApp.isVersionAtLeast?.('6.1')) webApp.setBackgroundColor?.(APP_BACKGROUND);
      if (webApp.isVersionAtLeast?.('6.9')) webApp.setHeaderColor?.(APP_BACKGROUND);
      if (webApp.isVersionAtLeast?.('7.10')) webApp.setBottomBarColor?.(APP_BACKGROUND);
    } catch {
      // старые клиенты — останутся цвета темы
    }
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('gesturestart', (e) => e.preventDefault());
    document.addEventListener('gesturechange', (e) => e.preventDefault());
    document.addEventListener('gestureend', (e) => e.preventDefault());
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) e.preventDefault();
      lastTouchEnd = now;
    }, { passive: false });
  }
};

/**
 * Подтверждение необратимого действия попапом Telegram. true, если нажали «ok».
 * Без Telegram (браузер на стенде) — `window.confirm`. Текст должен называть, что
 * именно произойдёт: сумму, получателей, что пропадёт. Telegram режет сообщение
 * длиннее 256 символов, заголовок длиннее 64.
 */
export const confirmAction = (
  message: string,
  options?: { title?: string; okText?: string; destructive?: boolean },
): Promise<boolean> => {
  const tg = getWebApp();
  if (!tg?.showPopup || !tg.isVersionAtLeast?.('6.2')) {
    return Promise.resolve(typeof window !== 'undefined' ? window.confirm(message) : false);
  }
  return new Promise<boolean>((resolve) => {
    try {
      tg.showPopup(
        {
          title: options?.title,
          message,
          buttons: [
            {
              id: 'ok',
              type: options?.destructive === false ? 'default' : 'destructive',
              text: options?.okText ?? 'Подтвердить',
            },
            { id: 'cancel', type: 'cancel' },
          ],
        },
        (buttonId) => resolve(buttonId === 'ok'),
      );
    } catch {
      resolve(false);
    }
  });
};

export const haptic = (kind: 'light' | 'success' | 'warning' | 'error' | 'selection' = 'light'): void => {
  const tg = getWebApp();
  if (!tg?.HapticFeedback) return;
  if (kind === 'selection') tg.HapticFeedback.selectionChanged();
  else if (kind === 'light') tg.HapticFeedback.impactOccurred('light');
  else tg.HapticFeedback.notificationOccurred(kind);
};
